import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SignOut, Clock, Plus, Minus, WarningCircle, CheckCircle, ClockCounterClockwise,
} from '@phosphor-icons/react';
import { kioskApi, kioskSession } from '../utils/kioskSession';
import './KioskPortalPage.css';

// Mirrors auth.py's KIOSK_IDLE_TIMEOUT_MINUTES (3 min) — warn a little
// before the backend would actually cut the session, so a real student
// isn't logged out mid-order with no warning.
const IDLE_TIMEOUT_SECONDS = 180;
const IDLE_WARNING_AT_SECONDS = 150;

function formatCurrency(amount) {
  return `GHS ${Number(amount || 0).toFixed(2)}`;
}

function titleCase(s) {
  return (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// A student without a personal phone/tablet scans their ID card + PIN
// (KioskLoginPage.js) to reach this screen and order their own canteen
// food — the kiosk session is confined to /api/canteen-wallet/* only
// (auth.py's get_current_user), so this is the ONLY thing a kiosk can do;
// grades/attendance/assignments were deliberately dropped from here (that's
// for home/library instead).
function KioskPortalPage() {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [items, setItems] = useState([]);
  const [cart, setCart] = useState({});
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  const [myOrders, setMyOrders] = useState(null);
  const [showOrders, setShowOrders] = useState(false);
  const [idleSeconds, setIdleSeconds] = useState(0);

  const studentFirstName = kioskSession.getStudentFirstName();

  const endSession = useCallback((message) => {
    const token = kioskSession.getToken();
    kioskSession.clear();
    if (token) kioskApi.logout(token);
    navigate('/kiosk', { state: message ? { message } : undefined });
  }, [navigate]);

  // Bounce to the login screen immediately if there's no session at all
  // (e.g. a bookmarked/back-button visit to this page).
  useEffect(() => {
    if (!kioskSession.getToken()) {
      navigate('/kiosk');
    }
  }, [navigate]);

  // Idle timer — any real interaction resets it; hitting the timeout logs
  // the kiosk out so the next student in line doesn't inherit this session.
  const idleSecondsRef = useRef(0);
  useEffect(() => {
    const resetIdle = () => { idleSecondsRef.current = 0; };
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((evt) => window.addEventListener(evt, resetIdle));
    const interval = setInterval(() => {
      idleSecondsRef.current += 1;
      setIdleSeconds(idleSecondsRef.current);
      if (idleSecondsRef.current >= IDLE_TIMEOUT_SECONDS) {
        endSession('You were logged out after being idle. Scan your ID card to start a new session.');
      }
    }, 1000);
    return () => {
      events.forEach((evt) => window.removeEventListener(evt, resetIdle));
      clearInterval(interval);
    };
  }, [endSession]);

  // Periodically confirm the backend hasn't already ended this session
  // (absolute timeout, or logged out from elsewhere) so the UI doesn't sit
  // showing stale data indefinitely.
  useEffect(() => {
    const check = async () => {
      const token = kioskSession.getToken();
      if (!token) return;
      try {
        const result = await kioskApi.checkSession(token);
        if (!result.valid) {
          kioskSession.clear();
          navigate('/kiosk', { state: { message: 'Your session ended. Scan your ID card to start a new one.' } });
        }
      } catch (_) { /* transient network issue — the next real request will surface it */ }
    };
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, [navigate]);

  const loadWallet = useCallback(() => {
    kioskApi.get('/api/canteen-wallet/student/summary')
      .then(setWallet)
      .catch((err) => { if (err.status === 401) endSession('Your session expired. Please scan your ID card again.'); });
  }, [endSession]);

  useEffect(() => {
    loadWallet();
    kioskApi.get('/api/canteen-wallet/items')
      .then((data) => setItems(Array.isArray(data) ? data.filter((i) => i.is_active) : []))
      .catch((err) => { if (err.status === 401) endSession('Your session expired. Please scan your ID card again.'); });
  }, [loadWallet, endSession]);

  const loadMyOrders = () => {
    kioskApi.get('/api/canteen-wallet/orders', { limit: 10 })
      .then((data) => setMyOrders(Array.isArray(data) ? data : []))
      .catch(() => {});
  };

  const blockedCategories = wallet?.blocked_categories || [];
  const cartTotal = items.reduce((sum, item) => sum + (cart[item.id] || 0) * item.price, 0);
  const cartLines = Object.entries(cart).filter(([, qty]) => qty > 0).map(([item_id, quantity]) => ({ item_id, quantity }));

  const setQty = (itemId, qty) => {
    setCart((prev) => ({ ...prev, [itemId]: Math.max(0, qty) }));
  };

  const placeOrder = async () => {
    if (!cartLines.length) return;
    setSubmitting(true);
    try {
      const result = await kioskApi.post('/api/canteen-wallet/orders', { items: cartLines, note: note || undefined });
      setLastOrder(result.order);
      setCart({});
      setNote('');
      loadWallet();
    } catch (error) {
      if (error.status === 401) { endSession('Your session expired. Please scan your ID card again.'); return; }
      setLastOrder({ error: error.message || 'Could not place order' });
    } finally {
      setSubmitting(false);
    }
  };

  const idleWarning = idleSeconds >= IDLE_WARNING_AT_SECONDS;
  const secondsLeft = Math.max(0, IDLE_TIMEOUT_SECONDS - idleSeconds);

  return (
    <div className="kiosk-portal-page">
      <div className="kiosk-portal-topbar">
        <div className="kiosk-portal-greeting">Hi {studentFirstName}!</div>
        <button className="kiosk-portal-done" onClick={() => endSession()}>
          <SignOut size={16} /> Done
        </button>
      </div>

      {idleWarning && (
        <div className="kiosk-idle-banner">
          <Clock size={16} /> Still there? You'll be logged out in {secondsLeft}s of inactivity.
        </div>
      )}

      <div className="kiosk-portal-content">
        {wallet && (
          <div className="card kiosk-stat-card" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div className="kiosk-stat-label">Your Balance</div>
              <div className="kiosk-stat-value">{formatCurrency(wallet.balance)}</div>
            </div>
            <button className="btn btn-secondary" onClick={() => { setShowOrders((s) => !s); if (!myOrders) loadMyOrders(); }}>
              <ClockCounterClockwise size={16} /> My Orders
            </button>
          </div>
        )}

        {wallet?.frozen && (
          <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}><WarningCircle size={14} /> Your wallet is frozen — ask a parent to unfreeze it before ordering.</div>
        )}
        {wallet?.low_balance && !wallet?.frozen && (
          <div style={{ color: '#d97706', fontSize: 13, marginBottom: 12 }}><WarningCircle size={14} /> Low balance — ask a parent to top up soon.</div>
        )}

        {showOrders && (
          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <h4 style={{ marginTop: 0 }}>My Recent Orders</h4>
            {myOrders === null ? (
              <div className="text-muted">Loading…</div>
            ) : myOrders.length ? (
              <table className="table">
                <thead><tr><th>Order</th><th>Items</th><th>Total</th><th>Status</th></tr></thead>
                <tbody>
                  {myOrders.map((o) => (
                    <tr key={o.id}>
                      <td>{o.order_code}</td>
                      <td>{o.items.map((l) => `${l.quantity}x ${l.name}`).join(', ')}</td>
                      <td>{formatCurrency(o.total)}</td>
                      <td className="text-capitalize">{o.status}{o.status === 'ready' ? ` — code ${o.pickup_code}` : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="text-muted">No orders yet.</div>}
          </div>
        )}

        {lastOrder ? (
          <div className="card" style={{ padding: 16, textAlign: 'center' }}>
            {lastOrder.error ? (
              <>
                <WarningCircle size={32} style={{ color: '#dc2626' }} />
                <div style={{ marginTop: 8 }}>{lastOrder.error}</div>
              </>
            ) : (
              <>
                <CheckCircle size={32} weight="fill" style={{ color: '#16a34a' }} />
                <div style={{ fontWeight: 700, marginTop: 8 }}>{lastOrder.order_code} — {formatCurrency(lastOrder.total)}</div>
                {lastOrder.status === 'ready' ? (
                  <div style={{ color: '#6b7280', fontSize: 13, marginTop: 6 }}>
                    Ready now — show this code at the counter: <strong>{lastOrder.pickup_code}</strong>
                  </div>
                ) : (
                  <div style={{ color: '#6b7280', fontSize: 13, marginTop: 6 }}>Sent to the kitchen — you'll be called when it's ready.</div>
                )}
              </>
            )}
            <div style={{ marginTop: 12 }}>
              <button className="btn btn-secondary" onClick={() => setLastOrder(null)}>Order more</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gap: 8 }}>
              {items.map((item) => {
                const blocked = blockedCategories.includes(item.item_type);
                const qty = cart[item.id] || 0;
                return (
                  <div key={item.id} className="card" style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 10, opacity: blocked ? 0.5 : 1 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{item.name}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{titleCase(item.item_type)} · {formatCurrency(item.price)}{blocked ? ' · not available for you' : ''}</div>
                    </div>
                    <button className="btn btn-secondary" style={{ padding: '2px 8px' }} disabled={blocked || qty === 0} onClick={() => setQty(item.id, qty - 1)}><Minus size={12} /></button>
                    <span style={{ minWidth: 20, textAlign: 'center' }}>{qty}</span>
                    <button className="btn btn-secondary" style={{ padding: '2px 8px' }} disabled={blocked} onClick={() => setQty(item.id, qty + 1)}><Plus size={12} /></button>
                  </div>
                );
              })}
              {!items.length && <div className="text-muted">No menu items available right now.</div>}
            </div>

            {cartLines.length > 0 && (
              <input
                className="form-control"
                placeholder="Note (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                style={{ marginTop: 12 }}
              />
            )}

            <div className="card" style={{ padding: 16, marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>Total</div>
                <div style={{ fontWeight: 700, fontSize: 18 }}>{formatCurrency(cartTotal)}</div>
              </div>
              <button
                className="btn btn-primary"
                disabled={!cartLines.length || submitting || wallet?.frozen}
                onClick={placeOrder}
              >
                {submitting ? 'Placing order…' : 'Place Order'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default KioskPortalPage;
